<main>
  <loading-overlay [isLoading]="isLoading > 0"></loading-overlay>

  <div class="content">
    <div class="page__title">
      <div class="page__title_container">
        <h1>{{ ('Production_Inventory_Title' | translateWF: 'Production Inventory') || 'Production Inventory' }}</h1>
      </div>
    </div>

    <!-- View Mode Toggle and Actions -->
    <div class="row-between-center">
      <div class="row-start-center gap-3">
        <!-- View Mode Toggle: Rolls | Production Cards -->
        <p-selectButton [formControl]="viewModeControl" [options]="viewModeOptions" (onChange)="onViewModeChange($event)" optionLabel="label" optionValue="value" />
      </div>

      <div class="row-sm-start-center">
        @if (canExport) {
          <p-button type="button" icon="pi pi-file-excel" (onClick)="exportToExcel()" class="p-button-outlined"></p-button>
        }
      </div>
    </div>

    <!-- Production Inventory Filters -->
    <app-production-inventory-filters [class.hidden]="!showFilters" (isLoading)="onLoadingChange($event)" (totalRecords)="onTotalRecordsChange($event)" />

    <p-panel header="Results">
      <ng-template pTemplate="icons">
        <p-button
          [label]="(showFilters ? 'Common_Hide_Filters' : 'Common_Show_Filters') | translateWF: (showFilters ? 'Hide Filters' : 'Show Filters')"
          [icon]="showFilters ? 'pi pi-filter-slash' : 'pi pi-filter'"
          (click)="showFilters = !showFilters"
          variant="outlined"
          size="small"
        />
      </ng-template>
      <p-card data-variant="compact">
        <!-- No Results Message -->
        @if (!hasResults && isLoading === 0) {
          <p-message severity="secondary">{{ ('No_Production_Inventory' | translateWF: 'No records found') || 'No records found' }}</p-message>
        }
        <!-- Rolls Table -->
        @if (currentViewMode === 'roll' && rollResults.length > 0) {
          <p-table [value]="rollResults">
            <ng-template #header>
              <tr>
                <th>{{ 'Production_Roll_Number' | translateWF: 'Roll #' }}</th>
                <th>{{ 'Production_Fabric_Code' | translateWF: 'Fabric Code' }}</th>
                <th>{{ 'Production_Fabric_Description' | translateWF: 'Fabric Description' }}</th>
                <th>{{ 'Production_Location' | translateWF: 'Location' }}</th>
                <th>{{ 'Production_Status' | translateWF: 'Status' }}</th>
                <th>{{ 'Production_Category' | translateWF: 'Category' }}</th>
                <th>{{ 'Production_Quality' | translateWF: 'Quality' }}</th>
                <th>{{ 'Production_Weight_Lbs' | translateWF: 'Weight (lbs)' }}</th>
                <th>{{ 'Production_Final_Weight' | translateWF: 'Final Weight' }}</th>
                <th>{{ 'Production_Work_Order' | translateWF: 'Work Order' }}</th>
                <th>{{ 'Production_Card' | translateWF: 'Production Card' }}</th>
                <th>{{ 'Production_Knitting_Order' | translateWF: 'Knitting Order' }}</th>
                <th>{{ 'Production_Shipped' | translateWF: 'Shipped' }}</th>
              </tr>
            </ng-template>
            <ng-template #body let-item>
              <tr>
                <td>{{ item.number }}</td>
                <td>{{ item.expectedFabricCode || '-' }}</td>
                <td>{{ item.expectedFabricDescription || '-' }}</td>
                <td>
                  @if (item.currentLocationPathNames?.length) {
                    @for (segment of item.currentLocationPathNames; track $index; let last = $last) {
                      @if (last) {
                        <span>{{ segment | localized }}</span>
                      } @else {
                        <small>{{ segment | localized }} &gt; </small>
                      }
                    }
                  } @else {
                    <small>{{ (item.currentLocationName | localized) || '-' }}</small>
                  }
                </td>
                <td>{{ getRollStatusName(item.status) }}</td>
                <td>{{ getRollCategoryName(item.category) }}</td>
                <td>{{ getRollQualityName(item.quality) }}</td>
                <td>{{ item.lastRecordedWeightLbs | number: '1.2-2' }}</td>
                <td>{{ item.finalWeightLbs | number: '1.2-2' }}</td>
                <td>{{ item.workOrder?.number || '-' }}</td>
                <td>{{ item.currentProductionCard?.number || item.productionCard?.number || '-' }}</td>
                <td>{{ item.knittingOrder?.number || '-' }}</td>
                <td>
                  @if (item.shipped) {
                    <p-tag severity="success" value="{{ 'Yes' | translateWF: 'Yes' }}"></p-tag>
                  } @else {
                    <p-tag severity="secondary" value="{{ 'No' | translateWF: 'No' }}"></p-tag>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        }

        <!-- Production Cards Table -->
        @if (currentViewMode === 'productionCard' && productionCardResults.length > 0) {
          <p-table [value]="productionCardResults">
            <ng-template #header>
              <tr>
                <th>{{ 'Production_Card_Number' | translateWF: 'Card #' }}</th>
                <th>{{ 'Production_Work_Order' | translateWF: 'Work Order' }}</th>

                <th>{{ 'Production_Client_PO' | translateWF: 'Client PO' }}</th>
                <th>{{ 'Production_Client' | translateWF: 'Client' }}</th>
                <th>{{ 'Production_Brand' | translateWF: 'Brand' }}</th>
                <th>{{ 'CurrentLocationName' | translateWF: 'Current Location Name' }}</th>
                <th>{{ 'Production_Color' | translateWF: 'Color' }}</th>
                <th>{{ 'Production_Fabric_Code' | translateWF: 'Fabric' }}</th>
                <th>{{ 'Production_Fabric_Description' | translateWF: 'Fabric Description' }}</th>
                <th>{{ 'Production_Total_Rolls' | translateWF: 'Total Rolls' }}</th>
                <th>{{ 'Production_Total_Lbs' | translateWF: 'Total Lbs' }}</th>
                <th>{{ 'Production_Total_Yards' | translateWF: 'Total Yards' }}</th>
              </tr>
            </ng-template>
            <ng-template #body let-item>
              <tr>
                <td>{{ item.productionCard?.number || '-' }}</td>
                <td>{{ item.workOrder?.number || '-' }}</td>

                <td>{{ item.clientPO || '-' }}</td>
                <td>{{ item.client?.name || item.client?.code || '-' }}</td>
                <td>{{ item.brand?.name || item.brand?.code || '-' }}</td>
                <td>{{ (item.currentLocationName | localized) || '-' }}</td>
                <td>{{ item.colorName || '-' }}</td>
                <td>{{ item.fabricCode || '-' }}</td>
                <td>
                  {{ item.fabricDescription || '-' }}
                </td>
                <td>{{ item.totalRolls }}</td>
                <td>{{ item.totalLbs | number: '1.2-2' }}</td>
                <td>{{ item.totalYards | number: '1.2-2' }}</td>
              </tr>
            </ng-template>
          </p-table>
        }

        <!-- Pagination -->
        @if (hasResults) {
          <div class="row-between-center custom__table__pagination bg__white">
            <div class="row-start-center gap-1">
              <!-- First Page -->
              <p-button
                type="button"
                icon="pi pi-angle-double-left"
                [disabled]="isFirstPage"
                (onClick)="firstPage()"
                text
                [pTooltip]="'First_Page' | translateWF: 'First page'"
                tooltipPosition="top"
              />
              <!-- Previous Page -->
              <p-button
                type="button"
                icon="pi pi-chevron-left"
                [disabled]="isFirstPage"
                (onClick)="prevPage()"
                text
                [pTooltip]="'Previous_Page' | translateWF: 'Previous page'"
                tooltipPosition="top"
              />
              <!-- Page Input -->
              <div class="row-sm-start-center mx-2">
                <span class="text-sm">{{ 'Page' | translateWF: 'Page' }}</span>
                <p-inputNumber
                  [(ngModel)]="pageInputValue"
                  [min]="1"
                  [max]="totalPages"
                  [showButtons]="false"
                  inputStyleClass="w-16 text-center"
                  (onKeyDown)="onPageInputKeydown($event)"
                  (onBlur)="goToPage()"
                />
                <span class="text-sm">{{ 'Of' | translateWF: 'of' }} {{ totalPages }}</span>
              </div>
              <!-- Next Page -->
              <p-button
                type="button"
                icon="pi pi-chevron-right"
                [disabled]="isLastPage"
                (onClick)="nextPage()"
                text
                [pTooltip]="'Next_Page' | translateWF: 'Next page'"
                tooltipPosition="top"
              />
              <!-- Last Page -->
              <p-button
                type="button"
                icon="pi pi-angle-double-right"
                [disabled]="isLastPage"
                (onClick)="lastPage()"
                text
                [pTooltip]="'Last_Page' | translateWF: 'Last page'"
                tooltipPosition="top"
              />
              <!-- Refresh -->
              <p-button type="button" icon="pi pi-refresh" (onClick)="resetTable()" text [pTooltip]="'Reset' | translateWF: 'Reset'" tooltipPosition="top" />
            </div>
            <div class="row-start-center cs-paginator-rows-only">
              <span class="p-paginator-current">
                {{ 'Showing' | translateWF: 'Showing' }} {{ pageSize * page - pageSize + 1 | number }}
                {{ 'To' | translateWF: 'to' }}
                {{ (pageSize * page > totalRecords ? totalRecords : pageSize * page) | number }}
                {{ 'Of' | translateWF: 'of' }} {{ totalRecords | number }} {{ 'Items_Per_Page' | translateWF: 'Items per page' }}:
              </span>
              <p-paginator
                [rows]="pageSize"
                [totalRecords]="totalRecords"
                [showCurrentPageReport]="false"
                [showPageLinks]="false"
                [showJumpToPageDropdown]="false"
                [rowsPerPageOptions]="[25, 50, 100, 250]"
                (onPageChange)="setSearchPageSize($event)"
              />
            </div>
          </div>
        }
      </p-card>
    </p-panel>
  </div>
</main>
